import { LandingPageContent } from '../types';

const personalizedColoringBooks: LandingPageContent = {
	path: '/personalized-coloring-books',
	seoTitle: 'Personalized Coloring Books for Kids — Starring Your Child',
	metaDescription:
		'Custom coloring books where your child is the character on every page. Upload a photo, choose a theme, and we print outline pages ready to be filled in.',
	heading: 'Personalized Coloring Books',
	subheading: 'Every page is your child. The colouring-in is up to them.',
	heroImage: '/assets/hero/kid-2/flat_scene.png',
	intro: `A personalized coloring book takes the same idea as a personalized storybook and hands the last step to the child.

We generate clean outline illustrations of your child as the character in each scene, then print them as a proper coloring book. What arrives is a book of blank-but-recognisable pages: your child, on an adventure, waiting to be coloured in.`,
	sections: [
		{
			heading: 'Why children engage with these more',
			body: `A standard coloring book asks a child to colour in a stranger. A personalized one asks them to colour in themselves.

The practical difference is attention span. Children tend to work through pages more carefully when the character is them — there's an investment in getting their own hair colour right that simply doesn't exist for a generic cartoon rabbit.

It also gives the finished book a second life. Coloured-in pages get put on fridges and posted to grandparents in a way that plain ones do not.`,
		},
		{
			heading: 'What you get',
			body: `- Outline illustrations designed specifically for colouring — clean lines, generous open areas, no dense shading that would fight against a felt-tip.
- Your child as the recognisable character in every scene.
- Printed and bound, not emailed as a PDF to print at home.
- A review step before printing, so you see the pages first.`,
		},
		{
			heading: 'Best ages',
			body: `Roughly three to seven.

Under three, the fine motor control isn't there yet and the appeal is mostly in recognising themselves — still fun, but expect the colouring to be enthusiastic rather than accurate.

Over seven, children often want more detail and more challenge; the simpler outline pages can feel babyish. If you're buying for an older child, a personalized storybook or a printed poster of their character tends to land better.`,
		},
		{
			heading: 'Coloring book or storybook?',
			body: `Pick a **coloring book** if the child likes making things, if you want something that occupies them for more than one sitting, or if you're buying for a long journey or a hospital stay.

Pick a **storybook** if the gift is meant to be a keepsake, if it's for bedtime, or if the child is learning to read.

Plenty of people buy both — the same character can appear in each, which children find satisfying in a way that's hard to explain but obvious when you see it.`,
		},
	],
	picks: [
		{
			label: 'Browse coloring books',
			href: '/products',
			description: 'See available themes and page counts.',
		},
		{
			label: 'Personalized story books',
			href: '/personalized-story-books',
			description: 'The illustrated, full-colour version — best for bedtime and keepsakes.',
		},
	],
	faqs: [
		{
			question: 'How many pages is a personalized coloring book?',
			answer:
				'Page counts vary by product. Each coloring book listing shows its exact page count before you order.',
		},
		{
			question: 'Is the coloring book printed or a digital download?',
			answer:
				'Printed and shipped. The pages are produced on paper chosen to take crayon, pencil and felt-tip without bleeding through excessively.',
		},
		{
			question: 'What age is a personalized coloring book best for?',
			answer:
				'Roughly ages three to seven. Younger children enjoy recognising themselves but have limited fine motor control; older children usually prefer more detailed artwork or a storybook.',
		},
		{
			question: 'Can I use the same photo for a coloring book and a storybook?',
			answer:
				'Yes. Once an avatar has been generated for your child it can be reused across products, so the same character appears in both.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Gifts for 3-year-olds', href: '/gifts/gifts-for-3-year-olds' },
	],
	cta: { label: 'Create a coloring book', href: '/products' },
};

export default personalizedColoringBooks;
